import React, { ReactElement, useState } from 'react'

import {
  ModalProvider,
  TabPanel,
  TabularMenu,
  TabularMenuProvider,
} from '@joor/design-system'
import { Modal } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import usePortal from 'hooks/usePortal'
import { isLiteRetailer } from 'store/currentUser/selectors'

import ImportOrders, {
  IMPORT_SUMMARY,
  TEMPLATE_SELECTION,
} from './ImportOrders/ImportOrders'
import { ImportOrdersContextProvider } from './ImportOrders/ImportOrders.context'
import { ImportOrdersStandardContextProvider } from './ImportOrders/ImportOrdersStandard.context'
import { ImportOrdersVerticalHorizontalContextProvider } from './ImportOrders/ImportOrdersVerticalHorizontal.context'
import Template from './Templates/Templates'
import { ImportOrdersModalIds } from './orderImportModal.ids'

type Tab = {
  label: string
  id?: string
  component: ReactElement
  onClick?: () => void
}

const StyledModal = styled(Modal)<{ $wide?: boolean }>`
  position: absolute;
  top: 80px;
  // The next calc is to obtain 80px margin-bottom
  max-height: calc(100vh - 160px);
  text-align: left;
  padding: 8px 0 16px;

  > :last-child {
    padding: 0 16px;
    overflow: hidden;
  }

  &&& {
    // sc-247036: the Import Summary step shows a table with a File column
    // that can hold a long filename plus a linesheet/collection name, so it
    // gets extra width; the other steps (template selection, order
    // settings) keep the original, narrower width.
    width: ${({ $wide }) => ($wide ? '1100px' : '700px')};

    > span {
      padding: 0;
      top: 12px;
      right: 12px;
    }

    > header {
      padding: 16px;
      & > h2 {
        font-size: var(--size-font-medium);
        font-weight: var(--font-weight-medium);
        letter-spacing: var(--letter-spacing-wider);
        line-height: var(--line-height-20);
      }
    }
  }

  hr {
    display: block;
  }
`

const StyledTabularMenu = styled(TabularMenu)`
  margin: 0 0 16px;
  & > button {
    font-size: var(--font-size-small);
  }

  &.hidden {
    display: none;
    visibility: hidden;
  }
`

interface OrderImportModalProps {
  onClose: () => void
}

const OrderImportModal: React.FC<OrderImportModalProps> = ({ onClose }) => {
  const Portal = usePortal()
  const [modalTitle, setModalTitle] = useState<string>('')
  const [showTitleBar, setShowTitleBar] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState(0)

  const isLiteRetailerUser = useSelector(isLiteRetailer)

  const TABS: Array<Tab> = [
    {
      label: 'Import Orders',
      id: ImportOrdersModalIds.ImportOrdersTabId,
      component: (
        <ImportOrdersContextProvider
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        >
          <ImportOrdersVerticalHorizontalContextProvider>
            <ImportOrdersStandardContextProvider>
              <ImportOrders
                closeModal={onClose}
                setModalTitle={setModalTitle}
                setShowTitleBar={setShowTitleBar}
              />
            </ImportOrdersStandardContextProvider>
          </ImportOrdersVerticalHorizontalContextProvider>
        </ImportOrdersContextProvider>
      ),
    },
    ...(!isLiteRetailerUser
      ? [
          {
            label: 'Create Excel Orders Template',
            id: ImportOrdersModalIds.ExcelOrdersTemplateTabId,
            component: <Template />,
          },
        ]
      : []),
  ]

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          $wide={currentStep === IMPORT_SUMMARY}
          isOpen
          onClose={onClose}
          title={{
            id: ImportOrdersModalIds.HeaderId,
            text: showTitleBar ? modalTitle : '',
          }}
          disableBackgroundClick
        >
          <Modal.Content>
            <TabularMenuProvider tabs={TABS}>
              <StyledTabularMenu
                className={currentStep !== TEMPLATE_SELECTION ? 'hidden' : ''}
              />
              <TabPanel />
            </TabularMenuProvider>
          </Modal.Content>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

export default OrderImportModal
